/**
 * User Settings API hooks - Real-time sync with Firestore
 *
 * Settings are stored at /users/{userId} document with a `settings` field.
 */
import { useCallback, useMemo } from 'react';

import { useMutation } from '@tanstack/react-query';
import type { DocumentData } from 'firebase/firestore';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { useAtomValue } from 'jotai';

import { useFirestoreDocumentSync } from '@/hooks/useFirestoreSync';
import { DEFAULT_USER_SETTINGS } from '@/lib/settingsUtils';
import { auth, firestore } from '@/services/firebase';
import type { UserSettings } from '@/types/card';
import { settingsStateAtom } from '../state/settingsAtom';

// Re-export state atom for consumers who need direct access
export { settingsStateAtom } from '../state/settingsAtom';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Ensure user document exists with default settings (only for new users)
 */
async function ensureUserDocument(userId: string): Promise<void> {
  const userRef = doc(firestore, 'users', userId);

  // Check if document already exists with settings
  const snapshot = await getDoc(userRef);
  if (snapshot.exists() && snapshot.data()?.settings) {
    return;
  }

  // Document doesn't exist or has no settings - create with defaults
  await setDoc(userRef, { settings: DEFAULT_USER_SETTINGS }, { merge: true });
}

/**
 * Update user settings in Firestore
 */
async function updateUserSettings(settings: Partial<UserSettings>): Promise<void> {
  const userId = auth.currentUser?.uid;
  if (!userId) {
    throw new Error('User not authenticated');
  }

  const userRef = doc(firestore, 'users', userId);

  // Build update object with nested field paths for settings
  const updates: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(settings)) {
    updates[`settings.${key}`] = value;
  }

  await updateDoc(userRef, updates);
}

// ============================================================================
// Sync Hook
// ============================================================================

/**
 * Syncs user settings from Firestore to the settingsStateAtom.
 * Returns true while loading (until first snapshot arrives).
 *
 * @param enabled - Whether sync is enabled (typically: isAuthenticated)
 *
 * @example
 * ```tsx
 * const isLoadingSettings = useSettingsSync(isAuthenticated);
 * ```
 */
export function useSettingsSync(enabled: boolean): boolean {
  const transform = useCallback((docData: DocumentData | null): UserSettings | null => {
    if (!docData) return DEFAULT_USER_SETTINGS;
    const settings = docData.settings as UserSettings | undefined;
    return settings ? { ...DEFAULT_USER_SETTINGS, ...settings } : DEFAULT_USER_SETTINGS;
  }, []);

  const docRefFactory = useCallback(() => {
    const userId = auth.currentUser?.uid;
    if (!userId) return null;
    return doc(firestore, 'users', userId);
  }, []);

  // Ensure user document exists when enabled
  useMemo(() => {
    if (enabled) {
      const userId = auth.currentUser?.uid;
      if (userId) {
        void ensureUserDocument(userId);
      }
    }
  }, [enabled]);

  return useFirestoreDocumentSync({
    atom: settingsStateAtom,
    transform,
    docRefFactory,
    enabled,
  });
}

// ============================================================================
// Public Hooks
// ============================================================================

/**
 * Read-only hook for user settings.
 * Returns the current settings state from the shared Jotai atom.
 */
export function useUserSettingsQuery() {
  const state = useAtomValue(settingsStateAtom);
  return {
    data: state.data ?? undefined,
    isLoading: state.isLoading,
    error: state.error,
  };
}

/**
 * Mutation hook for updating user settings
 */
export function useUpdateSettingsMutation() {
  return useMutation({
    mutationFn: (settings: Partial<UserSettings>) => updateUserSettings(settings),
    onError: (err) => {
      console.error('Failed to update settings:', err);
    },
  });
}

/**
 * Combined hook for settings data and mutation
 */
export default function useUserSettings() {
  const { data: settings, isLoading, error } = useUserSettingsQuery();
  const updateMutation = useUpdateSettingsMutation();

  return {
    settings: settings ?? DEFAULT_USER_SETTINGS,
    isLoading,
    error,
    updateSettings: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
  };
}
